import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import type { VeilleConfiguration } from '../../store/models/types'

const VeilleManager = lazy(() => import('./VeilleManager'))

const VeilleContainer = () => {
  const dispatch = useDispatch()
  const addressData = useSelector((state: RootState) => state.retorikReducer.addressData)
  const veilleConfiguration = useSelector((state: RootState) => state.viewReducer.veilleConfiguration)
  const veilleEnabled = useSelector((state: RootState) => !!state.viewReducer.veilleConfiguration.enabled)
  const loaderClosed = useSelector((state: RootState) => state.retorikReducer.loaderClosed)
  const appAvailable = useSelector((state: RootState) => state.retorikReducer.appAvailable)
  const typingCount = useSelector((state: RootState) => state.directlineReducer.typingCount)

  const [applicationReady, setApplicationReady] = useState<boolean>(false)
  const [veilleLaunched, setVeilleLaunched] = useState<boolean>(false)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const veilleConfigurationRef = useRef<VeilleConfiguration>(veilleConfiguration)
  const veilleEnabledRef = useRef<boolean>(veilleEnabled)
  const veilleLaunchedRef = useRef<boolean>(false)

  useEffect(() => {
    if (loaderClosed || appAvailable) {
      setApplicationReady(true)
    }
  }, [loaderClosed, appAvailable])

  useEffect(() => {
    if (!veilleEnabled) {
      veilleEnabledRef.current = veilleEnabled
      timerRef.current && clearTimeout(timerRef.current)
      onClose()
    } else if (applicationReady) {
      veilleEnabledRef.current = veilleEnabled
    }
  }, [veilleEnabled, applicationReady])

  useEffect(() => {
    veilleLaunchedRef.current = veilleLaunched
  }, [veilleLaunched])

  const refreshTimer = async (): Promise<void> => {
    timerRef.current && clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      if (veilleConfigurationRef.current?.killConversationOnLaunch) {
        // Reset directline if a new one has already been recreated for the news inside the veille
        veilleConfigurationRef.current.views?.news?.enabled && (await dispatch(storeActions.directline.endConversation()))
        await dispatch(storeActions.directline.recreateDirectline({ addressData: addressData, userId: `veille_${Date.now()}`, skipWelcome: true }))
      }
      setVeilleLaunched(true)
    }, (veilleConfiguration.delayBeforeLaunchInSeconds || 120) * 1000)
  }

  useEffect(() => {
    return () => {
      timerRef.current && clearTimeout(timerRef.current)
    }
  }, [])

  /**
   * Refresh the timer of the veille on each typing activity emitted
   */
  useEffect(() => {
    if (!veilleLaunchedRef.current && veilleEnabledRef.current) {
      refreshTimer()
    }
  }, [typingCount])

  const onClose = async (): Promise<void> => {
    if (veilleLaunchedRef.current) {
      // Kill and recreate directline if needed
      if (veilleConfigurationRef.current?.killConversationOnLaunch) {
        // Reset directline if a new one has already been recreated for the news inside the veille
        veilleConfigurationRef.current.views?.news?.enabled && (await dispatch(storeActions.directline.endConversation()))
        await dispatch(storeActions.directline.recreateDirectline({ addressData: addressData }))
      }
      setVeilleLaunched(false)
    }
    veilleEnabledRef.current && refreshTimer()
  }

  return veilleLaunched ? (
    <Suspense>
      <VeilleManager onClose={onClose} />
    </Suspense>
  ) : null
}

export default VeilleContainer
